'use client';

import React from 'react';
import { Pill, AlertTriangle, XCircle, PackageX, Clock, Package } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface PharmacyStats {
  total: number;
  inStock: number;
  lowStock: number;
  outOfStock: number;
  expired: number;
  expiringSoon: number;
}

interface PharmacyHeaderProps {
  stats: PharmacyStats;
}

export default function PharmacyHeader({ stats }: PharmacyHeaderProps) {
  const cards = [
    {
      label: 'Total Medicines',
      value: stats.total,
      icon: Pill,
      color: 'text-primary',
      bg: 'bg-primary/10',
      border: 'border-primary/20',
    },
    {
      label: 'In Stock',
      value: stats.inStock,
      icon: Package,
      color: 'text-success',
      bg: 'bg-success/10',
      border: 'border-success/20',
    },
    {
      label: 'Low Stock',
      value: stats.lowStock,
      icon: AlertTriangle,
      color: 'text-warning',
      bg: 'bg-warning/10',
      border: 'border-warning/20',
    },
    {
      label: 'Out of Stock',
      value: stats.outOfStock,
      icon: XCircle,
      color: 'text-danger',
      bg: 'bg-danger/10',
      border: 'border-danger/20',
    },
    {
      label: 'Expired',
      value: stats.expired,
      icon: PackageX,
      color: 'text-danger',
      bg: 'bg-danger/10',
      border: 'border-danger/20',
    },
    {
      label: 'Expiring in 90d',
      value: stats.expiringSoon,
      icon: Clock,
      color: 'text-warning',
      bg: 'bg-warning/10',
      border: 'border-warning/20',
    },
  ];

  return (
    <div data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:74:4" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="74" data-component-end-line="107" data-component-file="PharmacyHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
      <div data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:75:6" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="75" data-component-end-line="88" data-component-file="PharmacyHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
        <div data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:76:8" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="76" data-component-end-line="79" data-component-file="PharmacyHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <h1 data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:77:10" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="77" data-component-end-line="77" data-component-file="PharmacyHeader.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-foreground%20tracking-tight%22%2C%22textContent%22%3A%22Pharmacy%20Inventory%22%7D" className="text-2xl font-bold text-foreground tracking-tight">Pharmacy Inventory</h1>
          <p data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:78:10" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="78" data-component-end-line="78" data-component-file="PharmacyHeader.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-0.5%22%2C%22textContent%22%3A%22Manage%20medicines%2C%20stock%20levels%2C%20expiry%20dates%2C%20and%20supplier%20information%22%7D" className="text-sm text-muted-foreground mt-0.5">Manage medicines, stock levels, expiry dates, and supplier information</p>
        </div>
        <div data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:80:8" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="80" data-component-end-line="87" data-component-file="PharmacyHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
          {(stats.lowStock > 0 || stats.outOfStock > 0) && (
            <span data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:82:12" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="82" data-component-end-line="85" data-component-file="PharmacyHeader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20gap-1.5%20text-xs%20font-600%20text-warning%20bg-warning%2F10%20border%20border-warning%2F30%20rounded-full%20px-3%20py-1.5%22%2C%22textContent%22%3A%22items%20need%20reorder%22%7D" className="flex items-center gap-1.5 text-xs font-600 text-warning bg-warning/10 border border-warning/30 rounded-full px-3 py-1.5">
              <AlertTriangle data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:83:14" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="83" data-component-end-line="83" data-component-file="PharmacyHeader.tsx" data-component-name="AlertTriangle" data-component-content="%7B%22elementName%22%3A%22AlertTriangle%22%7D" size={13} />
              {stats.lowStock + stats.outOfStock} items need reorder
            </span>
          )}
        </div>
      </div>

      <div data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:90:6" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="90" data-component-end-line="106" data-component-file="PharmacyHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20sm%3Agrid-cols-3%20lg%3Agrid-cols-6%20gap-3%22%7D" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:94:12" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="94" data-component-end-line="103" data-component-file="PharmacyHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={card.label}
              className={`rounded-xl border ${card.border} ${card.bg} px-4 py-3 flex flex-col gap-1`}
            >
              <div data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:98:14" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="98" data-component-end-line="101" data-component-file="PharmacyHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
                <span data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:99:16" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="99" data-component-end-line="99" data-component-file="PharmacyHeader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20font-500%22%7D" className="text-xs text-muted-foreground font-500">{card.label}</span>
                <Icon data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:100:16" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="100" data-component-end-line="100" data-component-file="PharmacyHeader.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" size={14} className={card.color} />
              </div>
              <span data-component-id="src/app/pharmacy/components/PharmacyHeader.tsx:102:14" data-component-path="src/app/pharmacy/components/PharmacyHeader.tsx" data-component-line="102" data-component-end-line="102" data-component-file="PharmacyHeader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`text-2xl font-bold ${card.color}`}>{card.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
